import { useState } from 'react';
import Permission from '../../components/Permission';
import { DEFAULT_API_ADMIN, PERMISSIONS } from '../../constants';

import { Box, Chip, Typography } from '@mui/material';
import { getChainId, getUserId, httpClientReactAdmin } from '../../lib';
import { ArrayField, ChipField, Datagrid, DateField, FunctionField, NumberField, ReferenceArrayField, ReferenceField, SelectField, Show, SimpleShowLayout, SingleFieldList, Tab, TabbedShowLayout, TextField } from 'ra-ui-materialui';
import { useGetMany, useListContext, usePermissions, useRecordContext } from 'react-admin';
import { AttendantGoal, AttendantGoalType } from '../../models';
import CloneAttendantGoalBoardButton from './CloneAttendantGoalBoardButton';
import DisableAttendantGoalBoardButton from './DisableAttendantGoalBoardButton';
import attendantGoalBoards from '.';
import { AttendantGoalBoardDaysOfWeek } from '../../models/attendantGoal';
import AttendantGoalAiInsight, { AiLocale } from '../../components/AttendantGoalAiInsight';
import { InsightResponse, unwrapData } from '../../models/attendantGoalOffer';
import BoardReadingEmpty from './BoardReadingEmpty';
import { BoardPeriod } from './readingOutlook';

const MixFuelConfig = ({ record }: { record: any }) => {
    const allIds = [...(record.numeratorFuelIds || []), ...(record.denominatorFuelIds || [])];
    const { data: fuels, isLoading } = useGetMany('fuels', { ids: allIds }, { enabled: allIds.length > 0 });

    if (isLoading) return <Typography variant="body2">Carregando...</Typography>;

    const fuelMap: Record<string, string> = (fuels || []).reduce((acc, f) => ({ ...acc, [f.id]: f.name }), {});
    const numeratorNames = (record.numeratorFuelIds || []).map(id => fuelMap[id] || id);
    const denominatorNames = (record.denominatorFuelIds || []).map(id => fuelMap[id] || id);

    if (!numeratorNames.length && !denominatorNames.length) return null;

    return (
        <Box display="flex" flexDirection="column" gap={0.5}>
            <Typography variant="caption" color="text.secondary" fontWeight={600}>
                Meta: {record.minimumAmount}% de
            </Typography>
            <Box display="flex" flexWrap="wrap" gap={0.5} alignItems="center">
                {numeratorNames.map((name, i) => (
                    <Chip key={i} label={name} size="small" color="primary" variant="outlined" />
                ))}
            </Box>
            <Typography variant="caption" color="text.secondary">no total de</Typography>
            <Box display="flex" flexWrap="wrap" gap={0.5} alignItems="center">
                {denominatorNames.map((name, i) => (
                    <Chip key={i} label={name} size="small" variant="outlined" />
                ))}
            </Box>
        </Box>
    );
};

const containerChipSx = {
    padding: 0,
};

const SingleFieldChips = (props) => {
    const { data } = useListContext();
    return (
        !data || !data.length ?
            <FunctionField render={() => props.emptyText || 'Sem informações'} />
            :
            <SingleFieldList>
                <ChipField source={props.source} />
            </SingleFieldList>
    );
};

const StationListField = (props) => {
    const record = useRecordContext();
    if (!record) { return null; }

    return (
        <SimpleShowLayout sx={containerChipSx} record={record}>
            <ReferenceArrayField label="Postos" reference="stations" source="stationIds">
                <SingleFieldChips source="name" emptyText="Sem postos" />
            </ReferenceArrayField>
        </SimpleShowLayout>
    );
}

const FuelListField = (props) => {
    const record = useRecordContext();
    if (!record) { return null; }

    return (
        <SimpleShowLayout sx={containerChipSx} record={record}>
            <ReferenceArrayField label="Combustíveis" reference="fuels" source="fuelIds">
                <SingleFieldChips source="name" emptyText="Sem combustíveis" />
            </ReferenceArrayField>
        </SimpleShowLayout>
    );
}

const DayOfWeekField = (props) => {
    const record = useRecordContext();
    if (!record) { return null; }

    return (
        <SimpleShowLayout sx={containerChipSx} record={{ id: record.id, daysOfWeek: AttendantGoal.convertScheduleBooleanToDaysOfWeek(record).reduce<any>((acc, day) => ([...acc, { name: AttendantGoalBoardDaysOfWeek.translateType(day) }]), []) }}>
            <ArrayField source="daysOfWeek" label="Dias da semana">
                <Datagrid bulkActionButtons={false}>
                    <TextField source="name" label="Dia" />
                </Datagrid>
            </ArrayField>
        </SimpleShowLayout>
    );
}

// Aba "Leitura da IA": sempre presente, vale para quadro pago ou de degustação.
// O fetch só acontece quando a aba abre (o Tab do react-admin monta o filho sob demanda).
// Sem semana completa ainda, mostra quando chega a primeira leitura e um exemplo (BoardReadingEmpty).
const BoardAiInsight = () => {
    const record = useRecordContext();
    if (!record?.id) { return null; }

    const load = async (locale: AiLocale) => {
        const { json } = await httpClientReactAdmin(`${DEFAULT_API_ADMIN}/attendant-goal-boards/${record.id}/ai-insight?locale=${locale}`);
        return unwrapData<InsightResponse>(json);
    };

    return <AttendantGoalAiInsight resetKey={String(record.id)} load={load} insufficientNode={<BoardReadingEmpty board={record as BoardPeriod} />} />;
};

const AttendantTargetList = (props) => {

    const { permissions } = usePermissions();

    const [chainId] = useState(getChainId());
    if (!chainId) { return <Typography>Selecione uma Rede</Typography>; }

    return (
        <Permission permission={PERMISSIONS.ATTENDANT_TARGET}>
            <Show title='Quadro' {...props}>
                <TabbedShowLayout>
                    <Tab label="Resumo">
                        <DisableAttendantGoalBoardButton style={{ marginTop: 20 }} isOutlined />
                        <CloneAttendantGoalBoardButton variant="outlined" style={{ marginBottom: 10 }} />
                        <TextField source="code" label="Código" />
                        <TextField source="name" label="Nome" />
                        <ReferenceField source="createdByEmployeeId" reference="employees" label="Criado por" link={permissions && permissions.includes(PERMISSIONS.UPDATE_USER)}>
                            <FunctionField render={record => record ? `${record.name} ${record.id === getUserId() ? '(você)' : ''}` : ''} />
                        </ReferenceField>
                        <StationListField />
                        <FuelListField />
                        <DateField source="startDate" label="Inicio em" textAlign="center" emptyText="--" />
                        <DateField source="endDate" label="Fim em" textAlign="center" emptyText="--" />
                        <DayOfWeekField />
                        <DateField source="disabledAt" label="Desativado em" showTime textAlign="center" emptyText="--" />
                    </Tab>
                    <Tab label="Metas">
                        <ArrayField source="targets" label="Metas">
                            <Datagrid bulkActionButtons={false}>
                                <SelectField
                                    source="type"
                                    label="Tipo"
                                    choices={AttendantGoalType.attendantGoalTypesChoices}
                                />
                                <FunctionField
                                    label="Mínimo / Meta"
                                    render={(record) => record?.type === AttendantGoalType.MIX
                                        ? `${Number(record.minimumAmount).toFixed(2)}%`
                                        : Number(record?.minimumAmount || 0).toFixed(2)
                                    }
                                    textAlign="center"
                                />
                                <FunctionField
                                    label="Configuração Mix"
                                    render={(record) => record?.type === AttendantGoalType.MIX
                                        ? <MixFuelConfig record={record} />
                                        : null
                                    }
                                />
                                <NumberField
                                    source="fixedPrice"
                                    label="R$ fixo"
                                    emptyText="R$ 0,00"
                                    options={{
                                        style: 'currency',
                                        currency: 'BRL',
                                        minimumFractionDigits: 2,
                                        maximumFractionDigits: 2
                                    }}
                                    textAlign="center"
                                />
                                <NumberField
                                    source="additionalUnit"
                                    label="Unid. adicional"
                                    emptyText="--"
                                    options={{
                                        minimumFractionDigits: 2,
                                        maximumFractionDigits: 2
                                    }}
                                    textAlign="center"
                                />
                                <NumberField
                                    source="additionalPrice"
                                    label="R$ adicional"
                                    emptyText="--"
                                    options={{
                                        style: 'currency',
                                        currency: 'BRL',
                                        minimumFractionDigits: 2,
                                        maximumFractionDigits: 2
                                    }}
                                    textAlign="center"
                                />
                            </Datagrid>
                        </ArrayField>
                    </Tab>
                    <Tab label="Leitura da IA">
                        <BoardAiInsight />
                    </Tab>
                </TabbedShowLayout>
            </Show>
        </Permission>
    );
};

export default AttendantTargetList;
